import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import ApiService from '@/services/apiService';
const itemTypeLoadMore = 'LOADMORE.';
// NOTE: should not go beyond the max-limit defined in the ABAC service
// will not lead to any faulty behaviour, but the calculated output of the load-more button may show incorrect values
const fetchLimit = 100;
const processOptionOpenAll = 'open-all';
const processOptionOffsetZero = 'offset-zero';
export default {
  name: 'CoreTenantScopeTree',
  props: {
    userId: {
      type: String,
      required: false,
      default: ''
    },
    subject: {
      type: String,
      required: false,
      default: 'UserManagement'
    },
    action: {
      type: String,
      required: false,
      default: 'manage'
    },
    activeScopeId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data: function () {
    return {
      itemTypeLoadMore: itemTypeLoadMore,
      scopeTree: [],
      fetchError: null,
      isLoading: false,
      searchItem: '',
      // to detect if the search-item is deleted and the default should be restored,
      // or to avoid firing the same query twice, store the last queried item here
      lastQueriedSearchItem: null,
      openItems: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    hasActiveSearch() {
      return (this.searchItem || '').trim().length >= 2;
    }
  },
  watch: {
    activeScopeId() {
      this.scopeTree = [];
      if (!this.searchItem.trim() && this.activeScopeId) {
        this.fetchSearchResult();
      } else {
        this.fetchChildren({});
      }
    },
    searchItem: debounce(function () {
      // a prior active search-item got deleted, so fetch the default tree again
      if (this.lastQueriedSearchItem && (this.searchItem || '').trim().length < 2) {
        this.lastQueriedSearchItem = null;
        // make sure to reset the scope-tree here, reset any "open item" states
        this.scopeTree = [];
        this.openItems = [];
        this.fetchChildren({});
        return;
      }
      if ((this.searchItem || '').trim().length >= 2) {
        this.fetchSearchResult();
      }
    }, 500)
  },
  mounted() {
    if (!this.searchItem.trim() && this.activeScopeId) {
      this.fetchSearchResult();
    } else {
      this.fetchChildren({});
    }
  },
  methods: {
    processFetchedScopes(scopeList, options = null) {
      scopeList.forEach(scope => {
        if ((options || []).indexOf(processOptionOpenAll) >= 0) {
          this.openItems.push(scope.id);
        }
        if (!scope.children && scope.childCnt > 0) {
          scope.children = [];
        }
        if ((scope.children || []).length === 0 && !scope.childCnt) {
          delete scope.children;
        }
        if (scope.children) {
          // remove all existing "load-more" button items
          scope.children = scope.children.filter(c => this.itemType(c) !== itemTypeLoadMore);
          this.processFetchedScopes(scope.children, options);

          // if children had been loaded already, but there are more in total, add a Load-More button at the end of the list
          // contains a reference to its parent, and the offset from which to start loading
          if (scope.childCnt !== undefined && scope.children.length > 0 && scope.children.length < scope.childCnt) {
            // use a zero offset if there is search is active, as all the trees are partial
            const shouldUseZeroOffset = (options || []).indexOf(processOptionOffsetZero) >= 0 || this.hasActiveSearch && scope.children.length < fetchLimit;
            const totalCount = scope.childCnt;
            const fetchedOffset = scope.children.length;
            const fetchedUpTo = fetchedOffset + fetchLimit;
            // do not show "Load 101 to 1337 of 503", do not go beyond the total count
            const upperLimit = Math.min(fetchedUpTo, totalCount);
            scope.children.push({
              id: `${itemTypeLoadMore}${scope.id}`,
              name: this.$i18n_t('DigitalTwin.DigitalTwin.loadMoreItems', {
                nextLower: fetchedOffset + 1,
                nextUpper: upperLimit,
                total: totalCount
              }),
              offset: shouldUseZeroOffset ? 0 : scope.children.length,
              ref: scope
            });
          }
        }
      });
    },
    // findParentInTree(parentId, scopeList) {
    //   let foundParent = scopeList.find((s) => s.id === parentId);
    //   if (foundParent) {
    //     return foundParent;
    //   }
    //   for (let i = 0; i < scopeList.length; i++) {
    //     if ((scopeList[i].children || []).length) {
    //       foundParent = this.findParentInTree(parentId, scopeList[i].children);
    //       if (foundParent) {
    //         return foundParent;
    //       }
    //     }
    //   }
    //   return null;
    // },
    async fetchChildren(item) {
      let loadingId = item.id;
      let offset = 0;
      let refScope = item;
      if (this.itemType(item) === itemTypeLoadMore) {
        loadingId = item.ref.id;
        offset = item.offset;
        refScope = item.ref;
      }
      this.isLoading = true;
      await ApiService.userScopesFetchChildren(this.userId, this.subject, this.action, loadingId, offset, fetchLimit).then(response => {
        this.fetchError = response.error;
        this.processFetchedScopes(response.scopes);
        if (!loadingId) {
          this.scopeTree = response.scopes;
          // always open root-items if re-fetching
          this.openItems = response.scopes.map(s => s.id);
        } else {
          if (offset === 0) {
            // reset the existing children
            refScope.children = [];
          }
          refScope.children.push(...response.scopes);
        }
        if (refScope) {
          refScope.childCnt = Math.max(refScope.childCnt, response.totalCount);
        }
        this.processFetchedScopes(this.scopeTree);
        this.isLoading = false;
      }).catch(err => {
        console.error('Could not fetch scope children', loadingId, err);
      });
    },
    fetchSearchResult() {
      this.isLoading = true;
      this.lastQueriedSearchItem = this.searchItem.trim();
      ApiService.userScopesFetchSearchResult(this.userId, this.subject, this.action, this.lastQueriedSearchItem, ['no-children'],
      // if no search-item was specified, try to load the hierarchy for the active scope
      !this.lastQueriedSearchItem && this.activeScopeId ? this.activeScopeId : undefined).then(response => {
        this.openItems = [];
        this.fetchError = response.error;
        // open all items to the leaves (where the actual search-results are)
        // as well as set all "Load more" buttons to start loading from offset zero
        // since we do not know where in the actual list the found results are, just load from the start
        this.processFetchedScopes(response.scopes, [processOptionOpenAll, processOptionOffsetZero]);
        this.scopeTree = response.scopes;
        this.isLoading = false;
      }).catch(err => {
        console.error('Could not query scope-search', this.lastQueriedSearchItem, err);
      });
    },
    async selectedScope(item) {
      let tenantName = undefined;
      // fetch account-info for this scope
      await ApiService.tenantGetById(item.tenant, ['no-descendents', 'bare']).then(res => {
        tenantName = res.name;
      }).catch(err => {
        console.error('Could not get tenant', item, err);
      });

      // emit the selected scope, but without any children (do not propagate a whole tree unnecessarily)
      this.$emit('update', {
        ...item,
        tenantName,
        children: undefined
      });
    },
    itemType(item) {
      if (`${item.id}`.startsWith(itemTypeLoadMore)) {
        return itemTypeLoadMore;
      }
      return null;
    },
    getScopeTreeIcon(scope) {
      const faIcon = `fa-${scope.id === scope.tenantScope ? 'user-friends' : 'key'}`;
      return !scope.parent ? `fas ${faIcon}` : `fal ${faIcon}`;
    },
    itemLabelClass(item) {
      let activeColor = this.color;
      let baseClass = 'selectable-label ';
      if (item.properties && item.properties.disabled) {
        activeColor = 'disabled-twin primary';
        baseClass += 'disabled-twin ';
      }
      return baseClass + (this.activeScopeId === item.id ? `${activeColor}--text text-bold` : '');
    }
  }
};